import { useState, useEffect, useMemo } from "react";
import { DatePicker } from "antd";
import { useSelector } from "react-redux";

import useCurrentPath, {
  downloadMonthWiseExpenseRequestReport,
} from "../../components/Constants";
import ExpenseMultitasking from "../../components/ExpenseMultitasking";
import moment from "moment";

import CustomLoader from "../../components/CustomLoader";

export default function ListOfExpense() {
  const { RangePicker } = DatePicker;
  const [showLoader, setShowLoader] = useState(false);
  const [searchFilterData, setSearchFilterData] = useState("");
  const currentPath = useCurrentPath();
  let storeRole = useSelector((state) => state.auth.role);
  let storeToken = useSelector((state) => state.auth.accessToken);
  let storeLIUId = useSelector((state) => state.auth.lIUId);
  const storeAccessTokenExpiresOn = useSelector(
    (state) => state.auth.accessTokenExpiresOn
  );

  const initialState = "ALL";
  const [currentStatus, setCurrentStatus] = useState(initialState);
  const storeList = useSelector(
    (state) => state.auth.list?.[currentPath + "_info"] || []
  );

  // Memoize filteredList
  const filteredList = useMemo(() => {
    // Ensure decrypted is an array before calling map
    if (Array.isArray(storeList)) {
      return storeList.map(({ key, id, ...rest }) => rest);
    }

    // Return an empty array if decryption fails or result is not an array
    return [];
  }, [storeList]);

  const [filteredData, setFilteredData] = useState([]);

  const [isFilteredDates, setIsFilteredDates] = useState(false);
  const [filterValues, setFilterValues] = useState(null);

  // Callback to get filtered data from ExpenseMultitasking
  const handleFilteredData = (data) => {
    setFilteredData(data);
  };

  // Use useEffect to update filteredData when filteredList changes
  useEffect(() => {
    if (!isFilteredDates && filteredData !== filteredList) {
      setFilteredData(filteredList);
    }
  }, [filteredList, isFilteredDates]);

  const handleMonthRangeChange = (range) => {
    setSearchFilterData("");
    if (range && range.length === 2) {
      const startMonth = range[0]?.format("YYYY-MM");
      const endMonth = range[1]?.format("YYYY-MM");

      setFilterValues({ startMonth, endMonth });
      setIsFilteredDates(true);
    } else {
      setFilterValues(null);
      setIsFilteredDates(false);
    }
  };

  const startMonth = moment("2023-11", "YYYY-MM");
  const endMonth = moment();
  const disabledDate = (current) => {
    return (
      current &&
      (current < startMonth.startOf("month") ||
        current > endMonth.endOf("month"))
    );
  };

  const handleTabClick = (status) => {
    setCurrentStatus(status);
  };

  const handleDownloadMWERReport = async () => {
    const hasSearchValue = document.querySelector(
      ".dashboardSearch span input"
    ).value;
    setShowLoader(true);
    downloadMonthWiseExpenseRequestReport(
      storeRole,
      storeToken,
      storeLIUId,
      storeAccessTokenExpiresOn,
      currentPath,
      currentStatus,
      hasSearchValue,
      setShowLoader
    );
  };

  return (
    <div className="container">
      <CustomLoader showLoader={showLoader} />
      <div className="card bgi-no-repeat card-stretch gutter-b border-2 rounded-lg p-5">
        <div className="d-flex justify-content-between">
          <h3 className="font-weight-bolder text-dark text-size-16 m-0">
            Expense Listing
          </h3>
          <div className="card-toolbar d-flex m-0">
            <div className="bg-gray-200 border border-light-dark mr-5 pl-3 rounded text-dark-custom">
              <span className="font-weight-bolder m-0 mr-3">Filter</span>
              <RangePicker
                picker="month"
                format="MMM YYYY"
                disabledDate={disabledDate}
                onChange={handleMonthRangeChange}
                className="border-0 h-100 rangepicker"
              />
            </div>

            <button
              className="border border-light-dark btn font-weight-bolder px-4 py-2 text-dark-custom bg-transparent export-excel-button download-button"
              onClick={() => {
                handleDownloadMWERReport();
              }}
            >
              <i className="fas fa-file-download mr-1"></i>Download Excel
            </button>
          </div>
        </div>
        <div className="table-area">
          <ul className="nav nav-tabs nav-tabs-line mb-5">
            <li className="nav-item">
              <a
                className="nav-link listOfExpenseNavItem active"
                data-toggle="tab"
                onClick={() => handleTabClick(initialState)}
              >
                <span className="nav-text text-dark-custom px-3 cursor-pointer">
                  All
                </span>
              </a>
            </li>

            {/* My Requests */}
            {storeRole !== process.env.REACT_APP_EROLE && (
              <li className="nav-item">
                <a
                  className="nav-link listOfExpenseNavItem"
                  data-toggle="tab"
                  onClick={() =>
                    handleTabClick([
                      process.env.REACT_APP_MY_REQUESTS?.toUpperCase(),
                    ])
                  }
                >
                  <span className="nav-text text-dark-custom px-3 cursor-pointer">
                    {process.env.REACT_APP_MY_REQUESTS}
                  </span>
                </a>
              </li>
            )}
            {/* Approved */}
            <li className="nav-item">
              <a
                className="nav-link listOfExpenseNavItem"
                data-toggle="tab"
                onClick={() =>
                  handleTabClick([
                    process.env.REACT_APP_APPROVED_STATUS?.toUpperCase(),
                  ])
                }
              >
                <span className="nav-text text-dark-custom px-3 cursor-pointer">
                  {process.env.REACT_APP_APPROVED_STATUS}
                </span>
              </a>
            </li>
            {/* Canceled */}
            <li className="nav-item">
              <a
                className="nav-link listOfExpenseNavItem"
                data-toggle="tab"
                onClick={() =>
                  handleTabClick([
                    process.env.REACT_APP_CANCELED_STATUS?.toUpperCase(),
                  ])
                }
              >
                <span className="nav-text text-dark-custom px-3 cursor-pointer">
                  {process.env.REACT_APP_CANCELED_STATUS}
                </span>
              </a>
            </li>
            {/* Draft */}
            <li className="nav-item">
              <a
                className="nav-link listOfExpenseNavItem"
                data-toggle="tab"
                onClick={() =>
                  handleTabClick([
                    process.env.REACT_APP_DRAFT_STATUS?.toUpperCase(),
                  ])
                }
              >
                <span className="nav-text text-dark-custom px-3 cursor-pointer">
                  {process.env.REACT_APP_DRAFT_STATUS}
                </span>
              </a>
            </li>
            {/* Payment Pending */}
            <li className="nav-item">
              <a
                className="nav-link listOfExpenseNavItem"
                data-toggle="tab"
                onClick={() =>
                  handleTabClick([
                    process.env.REACT_APP_TAB_PAYMENT_PENDING_STATUS?.toUpperCase(),
                  ])
                }
              >
                <span className="nav-text text-dark-custom px-3 cursor-pointer">
                  {process.env.REACT_APP_TAB_PAYMENT_PENDING_STATUS}
                </span>
              </a>
            </li>
            {/* Pending */}
            <li className="nav-item">
              <a
                className="nav-link listOfExpenseNavItem"
                data-toggle="tab"
                onClick={() =>
                  handleTabClick([
                    process.env.REACT_APP_PENDING_STATUS?.toUpperCase(),
                    process.env.REACT_APP_NORESPONSE_STATUS.replace(
                      " ",
                      ""
                    )?.toUpperCase(),
                    process.env.REACT_APP_RESUBMITTED_STATUS?.toUpperCase(),
                    process.env.REACT_APP_SEEK_ACK_STATUS.replace(
                      " ",
                      ""
                    )?.toUpperCase(),
                    process.env.REACT_APP_ACK_REC_STATUS.replace(
                      " ",
                      ""
                    )?.toUpperCase(),
                  ])
                }
              >
                <span className="nav-text text-dark-custom px-3 cursor-pointer">
                  {process.env.REACT_APP_PENDING_STATUS}
                </span>
              </a>
            </li>
            {/* Sentback */}
            <li className="nav-item">
              <a
                className="nav-link listOfExpenseNavItem"
                data-toggle="tab"
                onClick={() =>
                  handleTabClick([
                    process.env.REACT_APP_SENTBACK_STATUS?.toUpperCase(),
                  ])
                }
              >
                <span className="nav-text text-dark-custom px-3 cursor-pointer">
                  {process.env.REACT_APP_SENTBACK_STATUS}
                </span>
              </a>
            </li>
          </ul>

          <div className="table-responsive">
            <div className="tab-content" id="alltable-size">
              <ExpenseMultitasking
                recordLimit={
                  String(currentStatus) ===
                  process.env.REACT_APP_MY_REQUESTS.toUpperCase()
                    ? "20"
                    : "20"
                }
                status={currentStatus}
                showLoaderOnList="yes"
                isFilterSelected={filterValues}
                searchFilterData={searchFilterData}
                setSearchFilterData={setSearchFilterData}
                onFilteredData={handleFilteredData} // Pass callback function
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
